import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const navigate = useNavigate();

  // Parallax effect
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <div style={{ position: 'relative', height: 'calc(100vh - 72px)', minHeight: '500px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Background Image with Parallax */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '115%', // extra height for parallax
          backgroundImage: 'url(/indian_elephant_hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 65%',
          y,
          zIndex: -1
        }}
      >
        {/* Overlay to darken background slightly for text readability */}
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.28)' }} />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity, y: useTransform(scrollY, [0, 300], [0, 50]), textAlign: 'center', color: 'white', padding: '0 20px', maxWidth: '850px' }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.1 }}
      >
        <h1 style={{
          fontSize: 'clamp(2.2rem, 8vw, 5.5rem)',
          marginBottom: '1rem',
          fontFamily: 'var(--font-sans)',
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          textShadow: '0 4px 15px rgba(0,0,0,0.3)'
        }}>
          {t('home.hero_title')}
        </h1>
        <p style={{
          fontSize: 'clamp(1rem, 3vw, 1.35rem)',
          marginBottom: '3rem',
          fontWeight: 400,
          fontFamily: 'var(--font-sans)',
          lineHeight: 1.6,
          textShadow: '0 2px 8px rgba(0,0,0,0.4)',
          maxWidth: '700px',
          margin: '0 auto 2.5rem auto',
          padding: '0 1rem'
        }}>
          {t('home.hero_subtitle')}
        </p>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          {/* Solid Green Button */}
          <motion.button
            onClick={() => navigate('/wildlife')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#ea580c',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '1.05rem',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
          >
            {t('home.explore_wildlife')} <ArrowRight size={20} />
          </motion.button>

          {/* Glassmorphic Button */}
          <motion.button
            onClick={() => navigate('/trekking')}
            whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.25)' }}
            whileTap={{ scale: 0.95 }}
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '1rem 2.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '1.05rem',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              transition: 'background-color 0.3s ease'
            }}
          >
            {t('home.start_trekking')}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default HeroSection;
